import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import { AR_CASH_METRICS_TIME_ZONE } from "./ar-cash-metrics.contract";
import {
  AR_CASH_QUERY_MAX_BATCH,
  CashQueryOutcomeSchema,
  CashRangeSchema,
  CashResolvedRangeSchema,
  type CashQueryInput,
  type CashRange,
} from "./ar-cash-dashboard.grammar";

export * from "./ar-cash-dashboard.grammar";

/**
 * Cash Dashboard data: every widget's query in one request.
 *
 * Each query is validated on its own, so one stale or hand-edited widget
 * gets its own typed error (`not_in_catalog`, `unsupported_combination`, …)
 * and the rest of the page still loads. Queries without their own range use
 * the page range.
 */
export const ArCashDashboardQueryInputSchema = z.object({
  range: CashRangeSchema,
  queries: z
    .array(
      z.object({
        key: z.string().min(1).max(200),
        /** A `CashQueryInput`; parsed per key by the server. */
        query: z.unknown(),
      }),
    )
    .min(1)
    .max(AR_CASH_QUERY_MAX_BATCH)
    .refine(
      (queries) => new Set(queries.map((q) => q.key)).size === queries.length,
      { message: "Query keys must be unique" },
    ),
});

/** The batched request, with each query typed as written. */
export interface ArCashDashboardQueryInput {
  range: CashRange;
  queries: { key: string; query: CashQueryInput }[];
}

export const ArCashDashboardQueryResponseSchema = z.object({
  timeZone: z.literal(AR_CASH_METRICS_TIME_ZONE),
  /** The page range, resolved to days. */
  range: CashResolvedRangeSchema,
  /** One outcome per query key. */
  results: z.record(z.string(), CashQueryOutcomeSchema),
});
export type ArCashDashboardQueryResponse = z.infer<
  typeof ArCashDashboardQueryResponseSchema
>;

export const arCashDashboardContracts = {
  query: oc
    .input(ArCashDashboardQueryInputSchema)
    .output(ArCashDashboardQueryResponseSchema)
    .route({
      method: "POST",
      summary: "Run AR cash dashboard queries",
      description:
        "Runs a batch of keyed AR cash queries (aggregate measures by dimensions, or named row lists) over Pacific calendar days, up to a year, and returns each key's result or typed error. The grammar is fixed: measures and lists come from the cash metrics catalog, and anything outside it is an error, never an approximation.",
    }),
} as const;
